import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ArrowRight, Clock, User, Loader2, ChevronRight, Cloud, Layers, Mail, CheckCircle2,
} from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import RegistrationMark from '../components/RegistrationMark';
import { BlogHeroVisual } from '../components/blog/BlogDiagrams';
import BlogLeadForm from '../components/blog/BlogLeadForm';
import BlogShare from '../components/blog/BlogShare';
import { useBlogPost } from '../hooks/useApi';

const DEFAULT_OG_IMAGE = 'https://images.unsplash.com/photo-1693031630146-568e2f72db0e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const upsertMeta = (key, content, attr = 'property') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

// Minimal inline renderer: **bold** and [text](url) (internal or external).
const renderInline = (text) => {
  if (!text) return null;
  const nodes = [];
  const regex = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let key = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) {
      nodes.push(<strong key={`b-${key++}`} className="font-black text-[#050F2E]">{m[2]}</strong>);
    } else if (m[3]) {
      const href = m[5];
      const label = m[4];
      if (href.startsWith('/')) {
        nodes.push(<Link key={`l-${key++}`} to={href} className="text-[#0A2A63] font-semibold underline decoration-[#00B7C7] underline-offset-2 hover:text-[#00B7C7]">{label}</Link>);
      } else {
        nodes.push(<a key={`l-${key++}`} href={href} target="_blank" rel="noreferrer" className="text-[#0A2A63] font-semibold underline decoration-[#00B7C7] underline-offset-2 hover:text-[#00B7C7]">{label}</a>);
      }
    }
    last = regex.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const RELATED_LINKS = [
  { to: '/printfactory', label: 'PrintFactory', desc: 'Cloud color workflow', Icon: Cloud, accent: '#00B7C7' },
  { to: '/impoproof', label: 'ImpoProof', desc: 'Double-sided proofing', Icon: Layers, accent: '#E5007E' },
  { to: '/contact', label: 'Contact', desc: 'Talk to a specialist', Icon: Mail, accent: '#FF4E50' },
];

const SectionBlock = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return <h2 className="text-2xl md:text-3xl font-black text-[#050F2E] tracking-tight mt-12 mb-4">{renderInline(block.text)}</h2>;
    case 'h3':
      return <h3 className="text-lg md:text-xl font-bold text-[#0A2A63] mt-8 mb-2">{renderInline(block.text)}</h3>;
    case 'image':
      return block.src ? (
        <figure className="my-8" data-testid="blog-image-block">
          <img src={block.src} alt={block.alt || ''} className="w-full rounded-2xl shadow-lg" />
          {block.caption ? (
            <figcaption className="mt-3 text-sm text-slate-500 text-center italic">{block.caption}</figcaption>
          ) : null}
        </figure>
      ) : null;
    case 'ul':
      return (
        <ul className="my-4 space-y-2">
          {block.items.map((it, i) => (
            <li key={`${it}-${i}`} className="flex items-start gap-3 text-slate-700 leading-relaxed">
              <CheckCircle2 className="h-5 w-5 text-[#00B7C7] flex-shrink-0 mt-0.5" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="my-4 space-y-2 counter-reset-list">
          {block.items.map((it, i) => (
            <li key={`${it}-${i}`} className="flex items-start gap-3 text-slate-700 leading-relaxed">
              <span className="h-6 w-6 rounded-full bg-[#0A2A63] text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ol>
      );
    case 'quote':
      return (
        <blockquote className="my-8 relative rounded-2xl bg-[#0A2A63] text-white p-7 overflow-hidden" data-testid="blog-central-idea">
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#FF4E50]/20 blur-3xl" />
          <p className="relative text-xl md:text-2xl font-black leading-snug tracking-tight">
            {block.text}
          </p>
          <span className="relative block mt-3 h-1 w-16 rounded-full bg-[#FF4E50]" />
        </blockquote>
      );
    case 'formula':
      return (
        <div className="my-4 inline-block rounded-xl bg-slate-100 border border-slate-200 px-5 py-3 font-mono text-[#0A2A63] font-bold">
          {block.text}
        </div>
      );
    case 'p':
    default:
      return <p className="my-4 text-[17px] text-slate-700 leading-8">{renderInline(block.text)}</p>;
  }
};

const CtaBanner = ({ cta }) => {
  const navigate = useNavigate();
  const isAnchor = cta.href?.startsWith('#');

  const onClick = () => {
    if (isAnchor) {
      const el = document.getElementById(cta.href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(cta.href);
    }
  };

  return (
    <div className="relative mt-14 rounded-3xl bg-white border-2 border-[#0A2A63]/10 p-8 md:p-10 shadow-xl overflow-hidden" data-testid="blog-cta-banner">
      <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#00B7C7]/15 blur-3xl" />
      <div className="relative max-w-2xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-2">Next step</p>
        <h3 className="text-2xl md:text-3xl font-black text-[#050F2E] tracking-tight">{cta.label}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">{cta.description}</p>
        <Button
          onClick={onClick}
          className="mt-7 inline-flex items-center justify-center w-full sm:w-auto whitespace-normal text-center bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-2xl min-h-12 py-3 px-8 font-bold btn-transition shadow-lg group"
          data-testid="blog-cta-button"
        >
          {cta.label}
          <ArrowRight className="h-4 w-4 ml-2 shrink-0 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </div>
  );
};

const RelatedSolutions = () => (
  <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-6" data-testid="blog-related-links">
    <p className="text-xs uppercase tracking-[0.25em] text-[#FF4E50] font-bold mb-4">Related solutions</p>
    <div className="space-y-3">
      {RELATED_LINKS.map(({ to, label, desc, Icon, accent }) => (
        <Link
          key={to}
          to={to}
          className="group flex items-center gap-3 rounded-xl bg-white border border-slate-100 p-3 hover:border-[#0A2A63] hover:shadow-md btn-transition"
          data-testid={`blog-related-${label.toLowerCase()}`}
        >
          <span className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}22` }}>
            <Icon className="h-4 w-4" style={{ color: accent }} />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-bold text-[#050F2E] leading-none">{label}</span>
            <span className="block text-xs text-slate-500 mt-0.5">{desc}</span>
          </span>
          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-[#0A2A63] group-hover:translate-x-0.5 transition-all" />
        </Link>
      ))}
    </div>
  </div>
);

const BlogArticle = () => {
  const { slug } = useParams();
  const { data: post, loading, error } = useBlogPost(slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Proof Nation`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', post.meta_description || '');
      const ogImage = post.hero_image || DEFAULT_OG_IMAGE;
      upsertMeta('og:title', post.title);
      upsertMeta('og:description', post.meta_description || post.excerpt);
      upsertMeta('og:type', 'article');
      upsertMeta('og:image', ogImage);
      upsertMeta('og:url', window.location.href);
      upsertMeta('og:site_name', 'Proof Nation');
      upsertMeta('twitter:card', 'summary_large_image', 'name');
      upsertMeta('twitter:title', post.title, 'name');
      upsertMeta('twitter:description', post.meta_description || post.excerpt, 'name');
      upsertMeta('twitter:image', ogImage, 'name');
    }
    return () => { document.title = 'Proof Nation'; };
  }, [post]);

  if (loading) {
    return (
      <main className="pt-40 pb-40 bg-white min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading article…</div>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="pt-40 pb-40 bg-white min-h-screen text-center">
        <h1 className="text-3xl font-black text-[#050F2E]">Article not found</h1>
        <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-[#0A2A63] font-bold">
          <ArrowLeft className="h-4 w-4" /> Back to the blog
        </Link>
      </main>
    );
  }

  return (
    <main className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden" data-testid="blog-article">
      <ColorAmbient variant="cool" intensity="soft" />

      <article className="container-pn relative">
        {/* Breadcrumb */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#0A2A63] btn-transition">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        {/* Header */}
        <header className="mt-6 max-w-3xl">
          <span className="inline-flex items-center bg-[#0A2A63]/5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#0A2A63]">
            {post.audience}
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black text-[#050F2E] tracking-tight leading-[1.03]" data-testid="blog-article-title">
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5 font-semibold text-[#0A2A63]"><User className="h-4 w-4" />{post.author}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />{post.read_minutes} min read</span>
          </div>
        </header>

        {/* Hero */}
        <div className="mt-8">
          <BlogHeroVisual post={post} className="shadow-2xl" />
        </div>

        {/* Body + sidebar */}
        <div className="mt-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8 min-w-0">
            {post.sections.map((block, i) => (
              <SectionBlock key={`${block.type}-${i}`} block={block} />
            ))}

            <CtaBanner cta={post.cta} />
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <BlogLeadForm slug={post.slug} download={post.download} />
              <BlogShare post={post} />
              <RelatedSolutions />
            </div>
          </aside>
        </div>
      </article>
    </main>
  );
};

export default BlogArticle;
